/**
 * @fileoverview The individual ranking card of the Sales Performance screen.
 * @description Loads the ranking for the selected period and team and shows it with its own
 * loading and error states, so a failed ranking never blanks the KPIs above it. A seller sees
 * "Your result" instead of a ranking, because their scope holds only themselves.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useTranslation } from 'react-i18next'

import PerformanceCard from '@/features/sales/components/PerformanceCard'
import PerformanceRankingTable from '@/features/sales/components/PerformanceRankingTable'
import TableSkeleton from '@/features/sales/components/TableSkeleton'
import { useSalesPerformanceRanking } from '@/features/sales/hooks/useSalesPerformance'

import type {
  SalesPerformanceQuery,
  SalesPerformanceScopeKind,
} from '@/features/sales/types/sales-performance.types'

interface PerformanceRankingCardProps {
  query: SalesPerformanceQuery
  scopeKind: SalesPerformanceScopeKind
}

/**
 * @component
 * @param {PerformanceRankingCardProps} props - The selection and the viewer's scope.
 * @param {SalesPerformanceQuery} props.query - The selected period and team.
 * @param {SalesPerformanceScopeKind} props.scopeKind - The scope the summary reported.
 */
function PerformanceRankingCard({ query, scopeKind }: PerformanceRankingCardProps) {
  const { t } = useTranslation()
  const ranking = useSalesPerformanceRanking(query)
  const title =
    scopeKind === 'SELF'
      ? t('sales.salesPerformance.ranking.yourResult')
      : t('sales.salesPerformance.ranking.title')

  return (
    <PerformanceCard
      title={title}
      testId="sales-performance-ranking"
      busy={ranking.isPlaceholderData}
      {...(ranking.data && {
        meta: t('sales.salesPerformance.ranking.meta', { count: ranking.data.total }),
      })}
    >
      {ranking.isPending && <TableSkeleton columns={6} rows={4} />}
      {ranking.isError && (
        <p
          role="alert"
          className="text-compact text-danger"
          data-testid="sales-performance-ranking-error"
        >
          {ranking.error.message}
        </p>
      )}
      {ranking.data && <PerformanceRankingTable ranking={ranking.data} />}
      {ranking.data && ranking.data.total > ranking.data.items.length && (
        <p className="text-xs text-slate" data-testid="sales-performance-ranking-cap-note">
          {t('sales.salesPerformance.ranking.capNote', {
            shown: ranking.data.items.length,
            total: ranking.data.total,
          })}
        </p>
      )}
    </PerformanceCard>
  )
}

export default PerformanceRankingCard
